import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ReactNode } from "react";
import {
	AlertTriangle,
	CheckCircle2,
	ExternalLink,
	ImageOff,
	Replace,
	ShieldAlert,
	XCircle,
} from "lucide-react";
import type { PageIssue } from "@/types/technicalAudit";

interface BrokenImageDetails {
	page_url?: string;
	source_url?: string;
	image_url?: string;
	target_url?: string;
	broken_url?: string;
	status_code?: number;
	http_status_code?: number;
	selector?: string;
	replacement_url?: string;
	new_image_url?: string;
}

interface BrokenImageFix {
	replacement_url?: string;
	new_image_url?: string;
	image_url?: string;
}

function EvidenceCheck({
	label,
	value,
	met,
}: {
	label: string;
	value: ReactNode;
	met: boolean;
}) {
	return (
		<div className="flex items-start gap-3 rounded-md border bg-card p-3">
			{met ? (
				<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
			) : (
				<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
			)}
			<div className="min-w-0 flex-1">
				<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{label}
				</div>
				<div className="mt-1 break-words text-sm text-foreground">{value}</div>
			</div>
		</div>
	);
}

export function BrokenImageCard({ issue }: { issue: PageIssue }) {
	const details = issue.issue_details as BrokenImageDetails;
	const proposedFix = issue.proposed_fix as BrokenImageFix | null;
	const sourceUrl = details.page_url || details.source_url || issue.page_url;
	const imageUrl = details.image_url || details.target_url || details.broken_url || issue.page_url;
	const statusCode = details.status_code || details.http_status_code;
	const replacementUrl =
		proposedFix?.replacement_url ||
		proposedFix?.new_image_url ||
		proposedFix?.image_url ||
		details.replacement_url ||
		details.new_image_url;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<ImageOff className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Broken Image</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This page references an image that could not be loaded. The safe fix is to restore the asset, replace the URL, or remove the image after checking the page.
					</p>
				</div>
				{statusCode && (
					<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
						HTTP {statusCode}
					</Badge>
				)}
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Broken Image URL
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={imageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-red-50 dark:bg-red-950/40/50 border-red-200 dark:border-red-800 hover:border-red-300 transition-all"
					>
						<p className="text-sm font-mono break-all text-red-700 dark:text-red-400 flex-1">{imageUrl}</p>
						<ExternalLink className="w-4 h-4 text-red-500 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
					{details.selector && (
						<p className="text-xs text-muted-foreground mt-2">Selector: {details.selector}</p>
					)}
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Source Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={sourceUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">{sourceUrl}</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>

			<Card className="border-amber-200 dark:border-amber-800 shadow-sm bg-amber-50 dark:bg-amber-950/40/40">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
							<ShieldAlert className="w-4 h-4 text-amber-700 dark:text-amber-400" />
						</div>
						Evidence Before Fix
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<EvidenceCheck
						label="Source page known"
						met={Boolean(sourceUrl)}
						value={sourceUrl || "Not available"}
					/>
					<EvidenceCheck
						label="Broken image URL confirmed"
						met={Boolean(imageUrl)}
						value={imageUrl || "Not available"}
					/>
					<EvidenceCheck
						label="HTTP status captured"
						met={Boolean(statusCode)}
						value={statusCode ? `HTTP ${statusCode}` : "Not available"}
					/>
					<EvidenceCheck
						label="Selector or page location"
						met={Boolean(details.selector)}
						value={details.selector || "Not captured yet. We need the exact image element or builder block before a real WordPress mutation is allowed."}
					/>
					<EvidenceCheck
						label="Replacement media"
						met={Boolean(replacementUrl)}
						value={replacementUrl || "Not chosen yet. The current safe action is restore, replace, remove, or recommendation only."}
					/>
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
							<Replace className="w-4 h-4 text-foreground" />
						</div>
						Safe Fix Options
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<p className="text-sm text-foreground">
							Restore the missing image, replace it with a chosen media URL, or remove the
						image after confirming the exact WordPress page/post content target.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
